import { EventType } from "@/analytics/analytics-events";
import { SelectPointCloudFileEventProperties } from "@/analytics/generic-events-properties";
import { ImageType } from "@/components/ui/tree/tree-context-menu/actions/upload-action";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { RelativeCrop } from "@/modes/create-area-mode/image-crop";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { selectActiveArea } from "@/store/selections-selectors";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import {
  computeRotatedImageDimensions,
  parseImageFromFile,
  resizeImage,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID, assert, getFileExtension } from "@faro-lotv/foundation";
import { isIElementAreaSection } from "@faro-lotv/ielement-types";
import { selectAncestor, selectIElement } from "@faro-lotv/project-source";
import { RegularImageSheetInfo } from "@faro-lotv/service-wires";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";
import { Vector3 } from "three";
import { UploadedFile } from "../file-upload-context/use-file-uploader";
import { useUploadMultiFiles } from "../file-upload-context/use-upload-multi-files";
import { CadUploadDialog } from "./cad-upload-dialog";
import { GaussianSplatsDialog } from "./gaussian-splats-upload-utils";
import {
  MAX_UPLOAD_CLOUD_FILE_SIZE_GB,
  MAX_UPLOAD_MODEL_FILE_SIZE_GB,
  PointCloudUploadDialog,
  SupportedPCFileExtensions,
  pointCloudTypeFromFileName,
} from "./point-cloud-upload-utils";
import { useUploadElement } from "./use-upload-element";
import { UploadElementType } from "./use-upload-element-types";

type SetUploadFileCallback = (file: File, onFileUploaded?: () => void) => void;

type UploadMultiImageFilesCallback = (
  /** The payload files */
  files: File[],

  /* The ID of the target section room selected in context menu */
  sectionRoomId: GUID,

  /** The type of image to be uploaded */
  imgType: ImageType,
) => void;

/** Uploads an image to new waypoint. */
type UploadWaypointWithImageFilesCallback = (
  /** The image files to upload. */
  files: File[],

  newWaypoint: {
    /** New waypoint id */
    id: GUID;
    /** Name of the new waypoint. */
    name: string;
    /** ID of the area to create a new room in. */
    areaId: GUID;
    /** The world pos of the new room section. */
    worldPosition: Vector3;
  },

  /** The type of image to be uploaded */
  imgType: ImageType,
) => Promise<void>;

/** Stores the parameters of an image sheet tiled */
export type ImgSheetTiledParam = {
  /** The payload file */
  file: File;
  /** The size of the tiles in pixels */
  tilesSize: number;
  /** The associated regular image sheet information */
  associatedRegularImageSheetInfo: RegularImageSheetInfo;
};

/**
 * Type defining the prototype of the function to use to upload a tiled image sheet file to an area
 *
 * @returns some information about image sheet tiled payload file uploaded
 */
type UploadAreaImageSheetTiledCallback = (
  /** image file name */
  name: string,

  /** area id */
  areaId: GUID,

  /** image sheet tiled parameters */
  imgSheetTiledParam: ImgSheetTiledParam,

  /** thumbnail image */
  thumbnail: File,

  /** Callback invoked when the file has been uploaded */
  onFileUploaded?: () => void,

  /** Selected PDF page number if the image sheet is created from a PDF file */
  selectedPdfPage?: number,
) => Promise<UploadedFile | void>;

/**
 * Type defining the prototype of the function to use to upload an image sheet file to an area
 *
 * @returns some information about area image sheet file uploaded
 */
type UploadAreaImageFileCallback = (
  /** image file name */
  name: string,

  /** area id */
  areaId: GUID,

  /** image rotation angle in degrees */
  rotationAngle: number,

  /** image cropping parameters in % from original image */
  crop?: RelativeCrop,

  /** Indicates that the associated mutation should not be created when set to true. When not defined, it defaults to false. */
  doNotCreateAssociatedMutation?: boolean,

  /** Selected PDF page number if the image sheet is created from a PDF file */
  selectedPdfPage?: number,

  ...commonArgs: Parameters<SetUploadFileCallback>
) => Promise<UploadedFile | void>;

export type ElementFileUploadContext = {
  /** Method allowing to set/change the currently selected Cad file */
  setCadFile: SetUploadFileCallback;

  /** Method allowing to set/change the currently selected Gaussian Splats file */
  setGaussianSplatsFile: SetUploadFileCallback;

  /** Method allowing to set/change the currently selected PointCloud file */
  setPointCloudFile: SetUploadFileCallback;

  /** A function to upload a regular image sheet for an area */
  uploadAreaImageFile: UploadAreaImageFileCallback;

  /** A function to upload a tiled image sheet for an area */
  uploadAreaImageSheetTiled: UploadAreaImageSheetTiledCallback;

  /** Creates a new waypoint and adds one or more image files to it. */
  uploadWaypointWithImageFiles: UploadWaypointWithImageFilesCallback;

  /** Method allowing to add multiple image data (2d or 360) to a location in the project */
  uploadMultiImageDataFiles: UploadMultiImageFilesCallback;
};

/**
 * * Helper function to rotate image before saving to backend
 *
 * @param file image file
 * @param angle angle to rotate image in degrees
 * @returns rotated image
 */
async function rotateImage(file: File, angle: number): Promise<File> {
  // Step 1: Create an image object from the file
  const img = await parseImageFromFile(file);

  const rotatedDimensions = computeRotatedImageDimensions(
    { width: img.width, height: img.height },
    angle,
  );

  // Step 2: Set up a canvas and rotate the image
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  assert(
    ctx && canvas,
    "Unable to create an offscreen 2d context to render the pdf image",
  );

  canvas.width = rotatedDimensions.width;
  canvas.height = rotatedDimensions.height;

  ctx.translate(rotatedDimensions.width / 2, rotatedDimensions.height / 2);
  ctx.rotate((angle * Math.PI) / 180);
  ctx.drawImage(img, -img.width / 2, -img.height / 2);

  // Step 3: Convert the canvas back to a file (Blob or File)
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      assert(blob, "Unable to convert rotated image to blob");
      const rotatedFile = new File([blob], file.name, { type: file.type });
      resolve(rotatedFile);
    }, file.type);
  });
}

/**
 * Helper function to crop image before saving to backend
 *
 * @param file image file
 * @param crop crop image parameters in %
 * @returns cropped image
 */
async function cropImage(file: File, crop: RelativeCrop): Promise<File> {
  // Create an image object from the file
  const img = await parseImageFromFile(file);

  // Create a canvas to draw the cropped image
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  assert(
    ctx && canvas,
    "Unable to create an offscreen 2d context to render the pdf image",
  );

  // get cropping parameters in pixels
  const cropWidth = 0.01 * crop.width * img.width;
  const cropHeight = 0.01 * crop.height * img.height;

  // Set canvas size to the cropped area
  canvas.width = cropWidth;
  canvas.height = cropHeight;

  // Draw the cropped image onto the canvas
  ctx.drawImage(
    img,
    0.01 * crop.x * img.width,
    0.01 * crop.y * img.height,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight,
  );

  // Convert the canvas back to a blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      assert(blob, "Unable to convert rotated image to blob");
      const rotatedFile = new File([blob], file.name, { type: file.type });
      resolve(rotatedFile);
    }, file.type);
  });
}

/**
 * Applies rotation and optional cropping to an input image file.
 *
 * @param image - The image file to be transformed.
 * @param rotation - The rotation angle (in degrees) to apply to the image. If 0, no rotation is performed.
 * @param crop - An optional cropping specification of type `RelativeCrop`. If provided, the image will be cropped after rotation.
 * @returns A Promise that resolves to a new `File` representing the transformed image.
 */
export async function transformImage(
  image: File,
  rotation: number,
  crop: RelativeCrop | undefined,
): Promise<File> {
  // If defined, apply the rotation and the cropping to the input image
  const rotatedImage =
    rotation === 0 ? image : await rotateImage(image, rotation);
  return crop ? await cropImage(rotatedImage, crop) : rotatedImage;
}

export const ElementFileUploadContext = createContext<
  ElementFileUploadContext | undefined
>(undefined);

/**
 * @returns A context for storing the selected file by the user
 *
 * While such storage logic would be preferred to be done inside the Redux store, this can't be done for object that
 * can't be serialized (like File objects)
 */
export function ElementFileUploadContextProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const { handleErrorWithToast } = useErrorHandlers();
  const store = useAppStore();

  const [gaussianSplatsFile, setGaussianSplatsFile] = useState<
    File | undefined
  >();
  const [onGaussianFileUploaded, setOnGaussianFileUploaded] =
    useState<() => void>();

  const [cadFile, setCadFile] = useState<File | undefined>();
  const [onCadFileUploaded, setOnCadFileUploaded] = useState<() => void>();

  const uploadElement = useUploadElement();
  const uploadMultiImageFiles = useUploadMultiFiles();

  const [pointCloudFile, setPointCloudFile] = useState<File | undefined>();
  const [onPointCloudUploaded, setOnPointCloudUploaded] =
    useState<() => void>();

  const activeArea = useAppSelector(selectActiveArea);

  const streamCadModelOutOfCoreGeneration = useAppSelector(
    selectHasFeature(Features.StreamCadModelOutOfCoreGeneration),
  );
  const handleCadFile = useCallback<SetUploadFileCallback>(
    (file, onFileUploaded) => {
      const fileSizeGB = file.size / 1024 ** 3;
      if (fileSizeGB > MAX_UPLOAD_MODEL_FILE_SIZE_GB) {
        handleErrorWithToast({
          title: `Input file larger than ${MAX_UPLOAD_MODEL_FILE_SIZE_GB}GB. Please try simplifying the model or uploading a subset of the model.`,
          error: "",
        });
        return;
      }

      if (streamCadModelOutOfCoreGeneration) {
        setCadFile(file);
        setOnCadFileUploaded(onFileUploaded);
      } else {
        uploadElement({
          type: UploadElementType.cad,
          file,
          name: file.name || "",
          createdAt: new Date(file.lastModified),
          onFileUploaded,
        });
      }
    },
    [streamCadModelOutOfCoreGeneration, handleErrorWithToast, uploadElement],
  );

  const handleWaypointWithImageDataFiles =
    useCallback<UploadWaypointWithImageFilesCallback>(
      async (files, newWaypoint, imgType) => {
        const thumbnailFiles = await Promise.all(
          files.map((file) => resizeImage(file)),
        );

        await uploadMultiImageFiles({
          type: UploadElementType.waypointWithImages,
          files,
          additionalFiles: thumbnailFiles,
          areaId: newWaypoint.areaId,
          newRoomWorldPos: newWaypoint.worldPosition,
          name: newWaypoint.name || "",
          imgType,
        });
      },
      [uploadMultiImageFiles],
    );

  const handleMultiImageDataFiles = useCallback<UploadMultiImageFilesCallback>(
    async (files, sectionRoomId, imgType) => {
      const sectionRoomElement = selectIElement(sectionRoomId)(
        store.getState(),
      );

      const area = selectAncestor(
        sectionRoomElement,
        isIElementAreaSection,
      )(store.getState());

      assert(area, "invalid area to upload 360 or 2D images");

      const thumbnailFiles = await Promise.all(
        files.map((file) => resizeImage(file)),
      );

      uploadMultiImageFiles({
        type: UploadElementType.imageData,
        files,
        additionalFiles: thumbnailFiles,
        areaId: area.id,
        parentElementId: sectionRoomId,
        imgType,
      });
    },
    [store, uploadMultiImageFiles],
  );

  /** Checking if the passed file object is a valid Gaussian Splats file (based on the extension) */
  const handleGaussianSplatsFile = useCallback<SetUploadFileCallback>(
    (file, onFileUploaded) => {
      setGaussianSplatsFile(file);
      setOnGaussianFileUploaded(onFileUploaded);
    },
    [],
  );

  const hasUnlimitedUpload = useAppSelector(
    selectHasFeature(Features.UnlimitedUpload),
  );

  /** Checking if the passed file object is a valid Point Cloud file (based on the extension) */
  const handlePointCloudFile = useCallback<SetUploadFileCallback>(
    (file, onFileUploaded) => {
      Analytics.track<SelectPointCloudFileEventProperties>(
        EventType.selectPointCloudFile,
        {
          fileSize: file.size,
          extension: getFileExtension(file.name) ?? "",
        },
      );

      try {
        pointCloudTypeFromFileName(file.name);

        const fileSizeGB = file.size / 1024 ** 3;
        if (!hasUnlimitedUpload && fileSizeGB > MAX_UPLOAD_CLOUD_FILE_SIZE_GB) {
          handleErrorWithToast({
            title: `File must be less than ${MAX_UPLOAD_CLOUD_FILE_SIZE_GB}GB`,
            error: "",
          });
          return;
        }

        setOnPointCloudUploaded(onFileUploaded);
        setPointCloudFile(file);
      } catch {
        setOnPointCloudUploaded(undefined);
        setPointCloudFile(undefined);

        const supportedExtensions = new Intl.ListFormat("en-US").format(
          Object.values(SupportedPCFileExtensions),
        );
        handleErrorWithToast({
          title: "Failed to import file",
          error: `The file extension is not supported. Only ${supportedExtensions} files are supported.`,
        });
      }
    },
    [hasUnlimitedUpload, handleErrorWithToast],
  );

  const handleAreaImageSheetTiled =
    useCallback<UploadAreaImageSheetTiledCallback>(
      async (
        name,
        areaId,
        imgSheetTiledParam,
        thumbnailFile,
        onFileUploaded,
        selectedPdfPage,
      ) => {
        try {
          return await uploadElement({
            type: UploadElementType.areaImageTiled,
            name,
            areaId,
            createdAt: new Date(imgSheetTiledParam.file.lastModified),
            file: imgSheetTiledParam.file,
            onFileUploaded,
            tilesSize: imgSheetTiledParam.tilesSize,
            additionalFiles: [thumbnailFile],
            associatedRegularImageSheetInfo:
              imgSheetTiledParam.associatedRegularImageSheetInfo,
            selectedPdfPage,
          });
        } catch {
          handleErrorWithToast({
            title: "Upload of the image sheet tiled failed. Please, try again",
            error: "",
          });
        }
      },
      [handleErrorWithToast, uploadElement],
    );

  const handleAreaImageFile = useCallback<UploadAreaImageFileCallback>(
    async (
      name,
      areaId,
      rotationAngle,
      crop,
      doNotCreateAssociatedMutation,
      selectedPdfPage,
      file,
      onFileUploaded,
    ) => {
      try {
        const transformedImage = await transformImage(
          file,
          rotationAngle,
          crop,
        );
        const thumbnailFile = await resizeImage(transformedImage);
        return uploadElement({
          type: UploadElementType.areaImage,
          file: transformedImage,
          additionalFiles: [thumbnailFile],
          areaId,
          name,
          createdAt: new Date(file.lastModified),
          doNotCreateAssociatedMutation,
          selectedPdfPage,
          onFileUploaded,
        });
      } catch {
        handleErrorWithToast({
          title: "Upload of the sheet failed. Please, try again",
          error: "",
        });
      }
    },
    [handleErrorWithToast, uploadElement],
  );

  const value = useMemo<ElementFileUploadContext>(
    () => ({
      setCadFile: handleCadFile,
      setGaussianSplatsFile: handleGaussianSplatsFile,
      setPointCloudFile: handlePointCloudFile,
      uploadAreaImageFile: handleAreaImageFile,
      uploadAreaImageSheetTiled: handleAreaImageSheetTiled,
      uploadWaypointWithImageFiles: handleWaypointWithImageDataFiles,
      uploadMultiImageDataFiles: handleMultiImageDataFiles,
    }),
    [
      handleAreaImageFile,
      handleAreaImageSheetTiled,
      handleCadFile,
      handleGaussianSplatsFile,
      handleMultiImageDataFiles,
      handlePointCloudFile,
      handleWaypointWithImageDataFiles,
    ],
  );

  // TODO: Offer better UX to upload point clouds without an area selected
  // https://faro01.atlassian.net/browse/SWEB-6017
  useEffect(() => {
    if (pointCloudFile && !activeArea) {
      handleErrorWithToast({
        title: "No Area selected",
        error: "Please first select an area to upload the point cloud to.",
      });

      setPointCloudFile(undefined);
    }
  }, [activeArea, handleErrorWithToast, pointCloudFile]);

  return (
    <ElementFileUploadContext value={value}>
      {children}
      {pointCloudFile && activeArea && (
        <PointCloudUploadDialog
          area={activeArea}
          file={pointCloudFile}
          setPointCloudFile={setPointCloudFile}
          onFileUploaded={onPointCloudUploaded}
        />
      )}
      {gaussianSplatsFile && (
        <GaussianSplatsDialog
          area={activeArea}
          file={gaussianSplatsFile}
          setGaussianSplatsFile={setGaussianSplatsFile}
          onFileUploaded={onGaussianFileUploaded}
        />
      )}
      {cadFile && (
        <CadUploadDialog
          file={cadFile}
          setCadFile={setCadFile}
          onFileUploaded={onCadFileUploaded}
        />
      )}
    </ElementFileUploadContext>
  );
}

/**
 * @returns The ElementFileUploadContext context
 */
export function useElementFileUploadContext(): ElementFileUploadContext {
  const context = useContext(ElementFileUploadContext);

  if (!context) {
    throw Error(
      "useElementFileUploadContext() must be used within an ElementFileUploadContext",
    );
  }

  return context;
}
